import React, {
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { v4 as uuid } from 'uuid';
import { DISCOUNT_TYPE, SERVICE_TYPE } from '~../__queries__';
import { useLogger, useSelector } from '~Hooks';
import { CardIdentity } from '~reducers';
import { CartItem, LocalItem, LocalVariation } from '~Types';
import { useStorage } from '~Components/Storage';
import { MEMBERSHIP_DISCOUNT_ID } from '~Components/LinkUser/consts';
import { useItemsPackager } from '~Utils/packagedItems';
import { getVatRate } from '~Scenes/SelfServiceKiosks/utils/vat';
import { EposMenuItem } from '../../types';

export interface ExpressCartDiscount {
  /** The ID of the offer that is used */
  id?: string;
  /** The type of discount (percentage or relative) */
  type: DISCOUNT_TYPE;
  /** The amount to discount */
  value: number;
  /** Which items to apply the discount to */
  itemIds?: string[];
  /** Title shown on the receipt */
  title?: string;
}

/**
 * A card tapped on the terminal at the start of the order, and who it belongs
 * to. `memberId` is null when the card was read fine but matched no member.
 */
export interface ExpressCartLoyalty {
  cardIdentity: CardIdentity;
  memberId: string | null;
  memberName: string | null;
}

/** Category id + UK VAT rate, registered by the product library once it has loaded. */
export interface UkVatCategory {
  id: string;
  ukVatRate: number;
}

/**
 * Everything that changes as the order is built up: adding/removing items,
 * applying a discount, switching service type. Components that read any of
 * this re-render on every cart change - only subscribe when you display it.
 */
interface ExpressCartState {
  items: LocalItem[];
  /** The items ready to be sent to the backend: prices calculated, discounts applied. */
  packagedItems: CartItem[];
  appliedDiscount: ExpressCartDiscount | null;
  serviceType: SERVICE_TYPE;
  /** Whether take-away orders are priced from a separate take-away menu. */
  useSeparateTakeAwayMenu: boolean;
  /** Sum of all items before discounts */
  total: number;
  /** Sum of all discounts */
  discount: number;
  /** Sum excluding VAT, after discounts */
  subtotal: number;
  /** True when any line is 18+ - the checkout asks the shopper to confirm before paying. */
  hasAgeRestrictedItems: boolean;
  /**
   * True when there is something to charge: at least one line and a total
   * above zero after discounts. A zero-amount order has no terminal step and
   * trips the payment machinery, so checkout is only offered when this holds.
   */
  payable: boolean;
}

/**
 * Identity details that change independently of the cart - a membership
 * lookup or a pickup time pick doesn't touch the items or the total. Kept in
 * its own context so the menu and product library don't re-render for them.
 */
interface ExpressCartIdentity {
  /** The user linked via customer lookup, e.g. a membership. */
  userId: string | null;
  /** Example #123 - assigned by OrderIdManager */
  easyId: string | null;
  /** Desired pickup time for the order */
  desiredTime: string | null;
  /** The card tapped for loyalty at the start of the order, null until (and unless) one was. */
  loyalty: ExpressCartLoyalty | null;
}

/**
 * Every way of changing the cart. The object is created once and never
 * replaced, so a component that only *acts* on the cart (a menu card, a
 * stepper) never re-renders because the cart changed.
 */
interface ExpressCartActions {
  /** Supports both setItems([...]) and setItems(state => [...]) */
  setItems: React.Dispatch<React.SetStateAction<LocalItem[]>>;
  setAppliedDiscount: React.Dispatch<React.SetStateAction<ExpressCartDiscount | null>>;
  setUserId: React.Dispatch<React.SetStateAction<string | null>>;
  setEasyId: React.Dispatch<React.SetStateAction<string | null>>;
  setDesiredTime: React.Dispatch<React.SetStateAction<string | null>>;
  /** Records the tapped card and its member (also sets userId), or clears both with null. */
  setLoyalty: (loyalty: ExpressCartLoyalty | null) => void;

  /** Adds an item to the cart, re-priced from the take-away menu when relevant. */
  addItem: (item: LocalItem) => void;
  /**
   * Adds a menu item as a fresh top-level cart line and returns that line's
   * id. This and `addUpsell` are the only places a menu item becomes a
   * LocalItem - components never build LocalItems themselves.
   */
  addMenuItem: (
    item: EposMenuItem,
    variations?: LocalVariation[],
    removedVariations?: LocalVariation[],
    /** Free text from the shopper, stored as the line's `message` (what POS and printing read). */
    comment?: string,
  ) => string;
  /**
   * Adds an upsell item attached to `parentCartId` - the parent's *cart line*
   * id, not its item or entry id - and returns the new line's id. An upsell
   * never enters the cart without its parent; that is the whole point of
   * this being a separate method.
   */
  addUpsell: (item: EposMenuItem, parentCartId: string) => string;
  /** Adds several items at once, e.g. an item together with its upsells. */
  addItems: (newItems: LocalItem[]) => void;
  /**
   * Applies a fresh set of variations and upsell picks to every parent id in
   * `ids` - "Ändra"/"Edit" on a cart line. Each parent keeps its cart id (so
   * the line doesn't fold away and back in), but its old upsells are dropped
   * and replaced with the new picks.
   */
  editMenuItem: (
    ids: string[],
    item: EposMenuItem,
    variations: LocalVariation[],
    removedVariations: LocalVariation[],
    upsellPicks: { item: EposMenuItem; count: number }[],
    /** Replaces the comment on every parent in `ids`. */
    comment?: string,
  ) => void;
  /** Removes an item from the cart */
  removeItem: (id: string) => void;
  /** Removes multiple items from the cart */
  removeItems: (ids: string[]) => void;
  /** Removes an item and every upsell attached to it */
  removeItemWithChildren: (id: string) => void;
  /** Empties the cart and resets everything attached to the order */
  clear: () => void;
  /** Whether the cart currently holds any items - read through a ref, so callers never subscribe to cart state just to check this. */
  hasItems: () => boolean;
  /** Sets a message on an item */
  setItemMessage: (id: string, message: string) => void;
  /** Replaces the variations on an item */
  setItemVariations: (
    id: string,
    variations: LocalItem['variations'],
    removedVariations: LocalItem['removedVariations'],
  ) => void;
  /** Duplicates a single line, without its upsells */
  duplicateItem: (id: string) => void;
  /**
   * Duplicates a line together with the upsells attached to it, `times`
   * over - "2 x burger with fries" gives two burgers that each carry fries.
   */
  duplicateItemWithChildren: (id: string, times: number) => void;
  /** Sets a discount on an item */
  setItemDiscount: (id: string, itemDiscount: number, discountTitle?: string) => void;
  /** Sets the service type and re-prices the cart when a separate take-away menu is in use. */
  updateServiceType: (type: SERVICE_TYPE) => void;
  /** Called by the product library so take-away VAT can be resolved inside addMenuItem. */
  registerUkVatCategories: (categories: UkVatCategory[]) => void;
}

const ExpressCartStateContext = createContext<ExpressCartState | undefined>(undefined);
const ExpressCartIdentityContext = createContext<ExpressCartIdentity | undefined>(undefined);
const ExpressCartActionsContext = createContext<ExpressCartActions | undefined>(undefined);

interface Props {
  children: ReactNode;
  useSeparateTakeAwayMenu?: boolean;
}

/** Re-prices an item from a menu entry, honouring volume-priced items (e.g. "0.5 Beer"). */
function priceFromEntry(item: LocalItem, entryPrice: number): LocalItem {
  if (item.volumeSuffix) {
    const number = item.title.match(/^\d+(\.\d+)?/);
    const count = number ? parseFloat(number[0]) : 1;
    return { ...item, price: entryPrice * count };
  }
  return { ...item, price: entryPrice };
}

function updateItem(list: LocalItem[], id: string, data: Partial<LocalItem>) {
  return list.map((item) => (item.id === id ? { ...item, ...data } : item));
}

/** One express cart. Mount once per kiosk flow. */
export const ExpressCartProvider: FC<Props> = ({ children, useSeparateTakeAwayMenu = false }) => {
  const logger = useLogger('epos-cart-provider');
  const demoMode = useSelector((state) => state.demoMode);
  const stateVenueId = useSelector((state) => state.venueId);
  const venueId = demoMode ? window.DEMO_MODE_VENUE_ID : stateVenueId;
  const posMenu = useSelector((state) => state.POSMenu);
  const takeAwayMenu = useSelector((state) => state.POSTakeAwayMenu);

  const [storageData, , storageLoading] = useStorage();
  const [serviceType, setServiceType] = useState<SERVICE_TYPE>(storageData.serviceType);
  const [items, setItems] = useState<LocalItem[]>([]);
  const [appliedDiscount, setAppliedDiscount] = useState<ExpressCartDiscount | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [easyId, setEasyId] = useState<string | null>(null);
  const [desiredTime, setDesiredTime] = useState<string | null>(null);
  const [loyalty, setLoyaltyState] = useState<ExpressCartLoyalty | null>(null);

  // Storage may still be loading on first render - pick up the stored default once it lands.
  useEffect(() => {
    if (!storageLoading) {
      setServiceType(storageData.serviceType);
    }
  }, [storageLoading]);

  /**
   * The actions read these through refs so the actions object itself can stay
   * the same for the provider's whole life - no consumer ever re-renders
   * because a menu or the service type changed.
   */
  const serviceTypeRef = useRef(serviceType);
  serviceTypeRef.current = serviceType;
  const posMenuRef = useRef(posMenu);
  posMenuRef.current = posMenu;
  const takeAwayMenuRef = useRef(takeAwayMenu);
  takeAwayMenuRef.current = takeAwayMenu;
  const ukVatCategoriesRef = useRef<UkVatCategory[]>([]);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  /** Take-away orders use the item's take-away VAT rate when one is set. */
  const itemsWithVatRate = useMemo(
    () =>
      items.map((item) => {
        const useTakeAwayVat =
          serviceType === SERVICE_TYPE.TAKE_AWAY &&
          item.takeAwayVatRate !== null &&
          item.takeAwayVatRate !== undefined;
        return { ...item, vatRate: useTakeAwayVat ? item.takeAwayVatRate! : item.vatRate };
      }),
    [items, serviceType],
  );

  const packagedItems = useItemsPackager(
    itemsWithVatRate,
    venueId || '',
    appliedDiscount?.value || null,
    appliedDiscount?.type || null,
    false,
    appliedDiscount?.itemIds,
    appliedDiscount?.id === MEMBERSHIP_DISCOUNT_ID,
    appliedDiscount?.id,
    appliedDiscount?.title,
  );

  useEffect(() => {
    logger.info('Express cart changed', { itemCount: items.length, serviceType });
  }, [items.length, serviceType]);

  const totals = useMemo(() => {
    const total = packagedItems.reduce((acc, item) => acc + item.amount, 0);
    const discount = packagedItems.reduce((acc, item) => acc + item.discount, 0);
    const subtotal = packagedItems.reduce((sum, item) => {
      const price = item.amount - item.discount;
      return sum + price / (1 + item.vatRate / 100);
    }, 0);
    return { total, discount, subtotal };
  }, [packagedItems]);

  /** Built exactly once - every dependency is read through a ref at call time. */
  const actions = useMemo<ExpressCartActions>(() => {
    /** Applies the take-away menu price to an item, if such a menu exists and applies. */
    const withTakeAwayPrice = (item: LocalItem): LocalItem => {
      const takeAway = takeAwayMenuRef.current;
      if (
        !useSeparateTakeAwayMenu ||
        !takeAway ||
        serviceTypeRef.current !== SERVICE_TYPE.TAKE_AWAY
      ) {
        return item;
      }
      if (item.manualPrice) {
        return item;
      }
      const entry = takeAway.menuEntries.find((e) => e.item.id === item.itemId);
      return entry ? priceFromEntry(item, entry.price) : item;
    };

    /** Menu item -> cart line. The one place this conversion happens. */
    const toLocalItem = (item: EposMenuItem, parentId?: string): LocalItem => {
      // Take-away in the UK is taxed at the category's UK rate.
      const vatRate = getVatRate({
        categoryId: item.categoryId,
        categoriesWithUkVatRate: ukVatCategoriesRef.current,
        itemVatRate: item.vatRate,
        serviceType: serviceTypeRef.current,
      });

      return {
        id: uuid(),
        parentId,
        itemId: item.id,
        categoryId: item.categoryId,
        title: item.title,
        description: item.description,
        imageUrl: item.imageUrl,
        price: item.priceValue,
        count: 1,
        fraction: 1,
        vatRate,
        ageRestricted: item.ageRestricted,
        modificationGroups: item.modificationGroups,
        allergyTags: item.allergyTags,
        variations: [],
        removedVariations: [],
      };
    };

    return {
      setItems,
      setAppliedDiscount,
      setUserId,
      setEasyId,
      setDesiredTime,

      setLoyalty: (next) => {
        logger.info('Setting express cart loyalty', {
          hasCard: Boolean(next),
          memberId: next?.memberId ?? null,
        });
        setLoyaltyState(next);
        setUserId(next?.memberId ?? null);
      },

      addItem: (item) => {
        logger.info('Adding item to express cart', { itemId: item.itemId, title: item.title });
        setItems((list) => [...list, withTakeAwayPrice(item)]);
      },

      addMenuItem: (item, variations = [], removedVariations = [], comment = '') => {
        const localItem = {
          ...toLocalItem(item),
          variations,
          removedVariations,
          message: comment || null,
        };
        logger.info('Adding menu item to express cart', {
          itemId: item.id,
          cartLineId: localItem.id,
          variationCount: variations.length,
          hasComment: Boolean(comment),
        });
        setItems((list) => [...list, withTakeAwayPrice(localItem)]);
        return localItem.id;
      },

      addUpsell: (item, parentCartId) => {
        if (!parentCartId) {
          // Programming error - never silently add an orphan upsell.
          logger.error('addUpsell called without a parent cart line', { itemId: item.id });
          throw new Error('addUpsell requires a parent cart line id');
        }
        const localItem = toLocalItem(item, parentCartId);
        logger.info('Adding upsell to express cart', {
          itemId: item.id,
          cartLineId: localItem.id,
          parentCartId,
        });
        setItems((list) => {
          if (!list.some((line) => line.id === parentCartId)) {
            logger.error('Upsell parent line is not in the cart - dropping the upsell', {
              itemId: item.id,
              parentCartId,
            });
            return list;
          }
          return [...list, withTakeAwayPrice(localItem)];
        });
        return localItem.id;
      },

      addItems: (newItems) => {
        setItems((list) => [...list, ...newItems.map(withTakeAwayPrice)]);
      },

      editMenuItem: (ids, item, variations, removedVariations, upsellPicks, comment = '') => {
        logger.info('Editing cart line', {
          itemId: item.id,
          parentCount: ids.length,
          variationCount: variations.length,
          upsellCount: upsellPicks.reduce((sum, pick) => sum + pick.count, 0),
          hasComment: Boolean(comment),
        });
        setItems((list) => {
          // Every parent's old upsells are dropped - the new picks replace them below.
          const withoutOldChildren = list.filter(
            (line) => !(line.parentId && ids.includes(line.parentId)),
          );
          const withNewVariations = withoutOldChildren.map((line) =>
            ids.includes(line.id)
              ? { ...line, variations, removedVariations, message: comment || null }
              : line,
          );
          const newChildren: LocalItem[] = [];
          ids.forEach((parentId) => {
            upsellPicks.forEach(({ item: upsellItem, count }) => {
              for (let n = 0; n < count; n += 1) {
                newChildren.push(withTakeAwayPrice(toLocalItem(upsellItem, parentId)));
              }
            });
          });
          return [...withNewVariations, ...newChildren];
        });
      },

      removeItem: (id) => {
        setItems((list) => list.filter((item) => item.id !== id));
      },

      removeItems: (ids) => {
        setItems((list) => list.filter((item) => !ids.includes(item.id)));
      },

      removeItemWithChildren: (id) => {
        setItems((list) => list.filter((item) => item.id !== id && item.parentId !== id));
      },

      clear: () => {
        logger.info('Clearing express cart');
        setItems([]);
        setAppliedDiscount(null);
        setUserId(null);
        setEasyId(null);
        setDesiredTime(null);
        setLoyaltyState(null);
      },

      hasItems: () => itemsRef.current.length > 0,

      setItemMessage: (id, message) => {
        setItems((list) => updateItem(list, id, { message }));
      },

      setItemVariations: (id, variations, removedVariations) => {
        setItems((list) => updateItem(list, id, { variations, removedVariations }));
      },

      duplicateItem: (id) => {
        const hash = uuid().substring(0, 6);
        setItems((list) => {
          const item = list.find((i) => i.id === id);
          return item ? [...list, { ...item, id: `${id}-duplicate-${hash}` }] : list;
        });
      },

      duplicateItemWithChildren: (id, times) => {
        if (times <= 0) {
          return;
        }
        logger.info('Duplicating cart line with children', { cartLineId: id, times });
        setItems((list) => {
          const parent = list.find((i) => i.id === id);
          if (!parent) {
            return list;
          }
          const children = list.filter((i) => i.parentId === id);
          const copies: LocalItem[] = [];
          for (let n = 0; n < times; n += 1) {
            const parentId = uuid();
            copies.push({ ...parent, id: parentId });
            children.forEach((child) => {
              copies.push({ ...child, id: uuid(), parentId });
            });
          }
          return [...list, ...copies];
        });
      },

      setItemDiscount: (id, itemDiscount, discountTitle) => {
        setItems((list) => updateItem(list, id, { discount: itemDiscount, discountTitle }));
      },

      updateServiceType: (type) => {
        logger.info('Updating express service type', { serviceType: type });
        const takeAway = takeAwayMenuRef.current;
        const eatHere = posMenuRef.current;

        if (useSeparateTakeAwayMenu && takeAway && type === SERVICE_TYPE.TAKE_AWAY) {
          setItems((list) =>
            list.map((item) => {
              if (item.manualPrice) {
                return item;
              }
              const entry = takeAway.menuEntries.find((e) => e.item.id === item.itemId);
              return entry ? priceFromEntry(item, entry.price) : item;
            }),
          );
        } else if (
          useSeparateTakeAwayMenu &&
          takeAway &&
          eatHere &&
          type === SERVICE_TYPE.EAT_HERE
        ) {
          setItems((list) =>
            list.reduce((acc: LocalItem[], item) => {
              if (item.manualPrice) {
                return [...acc, item];
              }
              const entry = eatHere.menuEntries.find((e) => e.item.id === item.itemId);
              if (!entry) {
                return acc;
              }
              // Discounts are cleared since they were calculated against the other price.
              return [...acc, { ...priceFromEntry(item, entry.price), discount: 0 }];
            }, []),
          );
        }

        setServiceType(type);
      },

      registerUkVatCategories: (categories) => {
        ukVatCategoriesRef.current = categories;
      },
    };
  }, [useSeparateTakeAwayMenu]);

  const hasAgeRestrictedItems = useMemo(() => items.some((item) => item.ageRestricted), [items]);
  const payable = items.length > 0 && totals.total - totals.discount > 0;

  const state = useMemo<ExpressCartState>(
    () => ({
      items,
      packagedItems,
      appliedDiscount,
      serviceType,
      useSeparateTakeAwayMenu,
      hasAgeRestrictedItems,
      payable,
      ...totals,
    }),
    [
      items,
      packagedItems,
      appliedDiscount,
      serviceType,
      useSeparateTakeAwayMenu,
      hasAgeRestrictedItems,
      payable,
      totals,
    ],
  );

  const identity = useMemo<ExpressCartIdentity>(() => ({ userId, easyId, desiredTime, loyalty }), [
    userId,
    easyId,
    desiredTime,
    loyalty,
  ]);

  return (
    <ExpressCartActionsContext.Provider value={actions}>
      <ExpressCartIdentityContext.Provider value={identity}>
        <ExpressCartStateContext.Provider value={state}>
          {children}
        </ExpressCartStateContext.Provider>
      </ExpressCartIdentityContext.Provider>
    </ExpressCartActionsContext.Provider>
  );
};

/**
 * Actions only. Use this in anything that changes the cart but does not
 * display it - menu cards, upsell cards, steppers, the detail pane. The
 * returned object is the same reference for the provider's whole life, so
 * it never causes a re-render and is safe in dependency arrays.
 */
export const useExpressCartActions = (): ExpressCartActions => {
  const context = useContext(ExpressCartActionsContext);
  if (!context) {
    throw new Error('useExpressCartActions must be used within an ExpressCartProvider');
  }
  return context;
};

/**
 * Cart state only, without the identity fields (userId/easyId/desiredTime)
 * that change independently of the cart. Use this where a component only
 * needs items/totals/service type - it skips re-rendering when a membership
 * lookup or pickup time changes, which `useExpressCart` does not.
 */
export const useExpressCartSummary = (): ExpressCartState => {
  const state = useContext(ExpressCartStateContext);
  if (!state) {
    throw new Error('useExpressCartSummary must be used within an ExpressCartProvider');
  }
  return state;
};

/**
 * State and actions together. Use this only where the cart is displayed
 * (drawer, bar, totals) - the component re-renders on every cart change.
 */
export const useExpressCart = () => {
  const state = useContext(ExpressCartStateContext);
  const identity = useContext(ExpressCartIdentityContext);
  const actions = useContext(ExpressCartActionsContext);
  if (!state || !identity || !actions) {
    throw new Error('useExpressCart must be used within an ExpressCartProvider');
  }
  return useMemo(() => ({ ...state, ...identity, ...actions }), [state, identity, actions]);
};

export default ExpressCartProvider;
